import { Link } from 'react-router-dom'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { isBusy } from '../../../Services/OrderWizard/writeState.ts'
import { trackWizardContinuedInClassic } from '../../../Services/OrderWizard/telemetry.ts'
import { filesAtHandOf, selectFileSummary } from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { DropNotice } from '../../../Services/OrderWizard/dropNotice.ts'
import { EditableOrderDetails } from './EditableOrderDetails.tsx'
import { AddFilesButton } from './AddFiles.tsx'
import { plural } from '../../../Util/plural.ts'
import { OverflowMenu } from './OverflowMenu.tsx'
import { DropNoticeLine } from './DropNoticeLine.tsx'

/** Uploads and writes carry on across steps, so they are shown with the order rather than in a step. */
const UploadStatus = ({ draft }: { draft: DraftState }) => {
  const summary = selectFileSummary(draft)
  const { total } = summary
  const busy = isBusy(draft, filesAtHandOf(draft))
  const attention = summary.failed + summary.needsReadd
  if (total === 0 && summary.itemsTotal === 0) {
    return null
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500" data-testid="wizard-status">
      {busy && <span className="loading loading-spinner loading-xs" />}
      <span data-testid="wizard-uploaded-count">
        Uploaded {summary.uploaded}/{total}
      </span>
      <span aria-hidden>·</span>
      <span data-testid="wizard-items-count">
        {summary.itemsCreated === summary.itemsTotal
          ? `${plural(summary.itemsTotal, 'item')} created`
          : `${summary.itemsCreated}/${summary.itemsTotal} items created`}
      </span>
      {attention > 0 && (
        <>
          <span aria-hidden>·</span>
          <span className="text-amber-700">
            {plural(attention, 'file')} need{attention === 1 ? 's' : ''} attention
          </span>
        </>
      )}
    </span>
  )
}

const PREVIEW_NOTE = 'Preview — feedback welcome; the classic view stays available.'

/**
 * The page's global bar: the order (renamed in place once it exists), the upload status, and the
 * actions that leave or replace the wizard, tucked into a menu so they do not compete with the step.
 */
export const WizardHeader = ({
  draft,
  onStartNew,
  notice,
  otherDrafts = []
}: {
  draft: DraftState
  onStartNew: () => void
  /** The last drop's confirmation; shown only for this draft. */
  notice?: DropNotice
  /** The user's other drafts in this browser, newest first, to switch to. */
  otherDrafts?: Pick<DraftState, 'id' | 'name'>[]
}) => {
  const dispatch = useDispatch()
  const hasFiles = draft.fileOrder.length > 0
  const canDiscard = hasFiles || !!draft.orderId
  return (
    <div className="flex items-start gap-3" data-testid="wizard-header">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
          <span>Order wizard</span>
          <span
            className="badge badge-xs badge-outline cursor-help text-slate-500"
            title={PREVIEW_NOTE}
            aria-label={PREVIEW_NOTE}
            data-testid="wizard-preview-badge"
          >
            preview
          </span>
          {draft.orderId && <UploadStatus draft={draft} />}
        </p>
        {draft.orderId ? (
          <EditableOrderDetails draft={draft} />
        ) : (
          <h1 className="text-base font-semibold text-slate-900">Create an order from FASTQ files</h1>
        )}
        <div aria-live="polite">{notice?.draftId === draft.id && <DropNoticeLine notice={notice} />}</div>
        {draft.storageFailed && (
          <p className="text-xs text-amber-700" data-testid="wizard-storage-failed">
            This draft can’t be kept in the browser; it continues on the server.
          </p>
        )}
      </div>
      {(draft.orderId || hasFiles) && <AddFilesButton draft={draft} />}
      {(draft.orderId || canDiscard || otherDrafts.length > 0) && (
        <OverflowMenu>
          {draft.orderId && (
            <li>
              <Link
                to={`/order/${draft.orderId}`}
                role="menuitem"
                data-testid="wizard-classic-view"
                onClick={() => trackWizardContinuedInClassic('header')}
              >
                Continue in classic view <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            </li>
          )}
          {hasFiles && (
            <li>
              <button type="button" role="menuitem" onClick={onStartNew} data-testid="wizard-new-draft">
                Start another order
              </button>
            </li>
          )}
          {otherDrafts.length > 0 && (
            <li className="menu-title text-xs" data-testid="wizard-other-drafts">
              Other drafts ({otherDrafts.length})
            </li>
          )}
          {otherDrafts.map((other) => (
            <li key={other.id}>
              <button
                type="button"
                role="menuitem"
                data-testid="wizard-switch-draft"
                onClick={() => dispatch(wizard.activeDraftSet({ draftId: other.id }))}
              >
                {other.name}
              </button>
            </li>
          ))}
          {canDiscard && (
            <li>
              <button
                type="button"
                role="menuitem"
                className="text-red-600"
                data-testid="wizard-discard"
                onClick={() => dispatch(wizard.draftDiscarded({ draftId: draft.id }))}
              >
                <span className="flex flex-col items-start">
                  Discard wizard draft
                  <span className="text-xs text-slate-500">An order already created stays under My Orders.</span>
                </span>
              </button>
            </li>
          )}
        </OverflowMenu>
      )}
    </div>
  )
}
